import {useNonReactiveCallback} from '@raison/ui-kit';
import {useEffect} from 'react';

type PollFn = () => Promise<void>;

type SharedPollingOptions = {
    interval: number;
};

type PollingOptions = SharedPollingOptions & {
    poll: PollFn;
};

type PollingEffectOptions = SharedPollingOptions & {
    poll: null | undefined | false | PollFn;
};

export function usePolling({interval, poll}: PollingEffectOptions) {
    const enabled = !!poll;
    poll = useNonReactiveCallback(poll || undefined);
    useEffect(() => {
        if (enabled) {
            const {teardown} = setupPolling({interval, poll});
            return teardown;
        }
    }, [interval]);
}

function setupPolling({interval, poll}: PollingOptions) {
    const execute = deduplicate(async () => {
        if (document.hidden === true) {
            console.log('[Version request] document.hidden is true; skipping request');
            return;
        }
        if (navigator.onLine === false) {
            console.log('[Version request] navigator.onLine is false; skipping request');
            return;
        }
        await poll();
    });

    const cleanups: Array<() => void> = [];
    cleanups[cleanups.length] = addListenerOn(document, 'visibilitychange', function onVisibilityChange() {
        execute();
    });
    cleanups[cleanups.length] = setIrregularInterval(function onInterval() {
        execute();
    }, interval);
    const teardown = () => cleanups.forEach(c => c());

    try {
        execute();
    } catch (err) {
        reportError(err);
    }
    return {teardown, execute};
}

type InferEventNames<T extends EventTarget> = keyof {
    [K in keyof T as K extends `on${infer E}` ? E : never]: void;
};

function addListenerOn<T extends EventTarget>(eventTarget: T, type: InferEventNames<T> & string, listener: () => void) {
    eventTarget.addEventListener(type, listener);
    return () => {
        eventTarget.removeEventListener(type, listener);
    };
}

function deduplicate(callback: () => Promise<void>) {
    let isInProgress = false;
    return async () => {
        if (isInProgress) {
            return;
        }
        isInProgress = true;
        try {
            return await callback();
        } finally {
            isInProgress = false;
        }
    };
}

function setIrregularInterval(callback: () => void, ms: number) {
    let timeoutId: number;
    const intervalId = window.setInterval(() => {
        const time = Math.random() * (ms * 0.25);
        timeoutId = window.setTimeout(() => {
            callback();
        }, time);
    }, ms);

    return () => {
        window.clearTimeout(timeoutId);
        window.clearInterval(intervalId);
    };
}
